// Desktop help / shortcuts panel — toggled with "?" (registered on the keymap
// at install, so no hub keydown edits) and from the "Controls & shortcuts" item
// in the account menu (ui/auth-panel.ts). Desktop-only: main skips this install
// path in VR, and the VR help panel in ui/help-panel.ts is untouched.
//
// Read-only quick reference: mouse + camera controls, mode switching, and the
// per-mode keyboard shortcuts. Rebindable keys are read LIVE from ui/hotkeys
// on every open, so the panel always shows the user's actual bindings; the
// fixed Cmd/Ctrl combos mirror the keymap commands in main.ts. Rebinding
// itself lives in the Settings overlay (the gear).
//
// While open it captures keydowns (same pattern as ui/settings-overlay.ts) so
// app shortcuts don't fire behind it; Esc or "?" closes. DOM-only: no scene
// change, so no wakeRendering needed.

import type { AppContext } from "../app/context";
import { keyForAction } from "./hotkeys";

const CSS = `
.dt-help {
  position: fixed; inset: 0; z-index: 60; display: flex; align-items: center;
  justify-content: center; background: rgba(0,0,0,0.45);
  font: 13px/1.45 system-ui, -apple-system, sans-serif; color: #eceff6;
}
.dt-help .card {
  width: 620px; max-width: calc(100vw - 32px); max-height: calc(100vh - 64px);
  overflow-y: auto; padding: 16px 20px 20px; border-radius: 14px;
  background: rgba(28,30,38,0.98); border: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
}
.dt-help .head {
  display: flex; align-items: center; justify-content: space-between;
  position: sticky; top: -16px; background: rgba(28,30,38,0.98);
  padding-top: 4px; margin: -4px 0 2px; z-index: 1;
}
.dt-help h3 { margin: 0; font-size: 16px; font-weight: 650; }
.dt-help .x {
  font: inherit; cursor: pointer; border: none; background: transparent;
  color: #aeb3c4; font-size: 18px; line-height: 1; padding: 4px 8px; border-radius: 6px;
}
.dt-help .x:hover { background: rgba(255,255,255,0.08); color: #fff; }
.dt-help .cols { display: flex; gap: 24px; align-items: flex-start; }
.dt-help .col { flex: 1 1 0; min-width: 0; }
.dt-help .sub {
  color: #8b90a3; font-size: 11px; text-transform: uppercase; letter-spacing: .05em;
  margin: 14px 0 4px;
}
.dt-help .r {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.06);
}
.dt-help .r .lab { color: #c7ccda; }
.dt-help kbd {
  font: 600 11px ui-monospace, SFMono-Regular, Menlo, monospace; color: #e7e9f0;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.16);
  border-bottom-width: 2px; border-radius: 5px; padding: 3px 8px; white-space: nowrap;
}
.dt-help .foot { color: #8b90a3; font-size: 11px; margin-top: 14px; }
`;

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  const s = document.createElement("style");
  s.textContent = CSS;
  document.head.appendChild(s);
  stylesInjected = true;
}

interface Row {
  keys: string;
  label: string;
}
interface Section {
  group: string;
  rows: Row[];
}

// The live key for a rebindable hotkey action, display-cased.
function k(id: string): string {
  const key = keyForAction(id);
  return key ? key.toUpperCase() : "unbound";
}

// Content is rebuilt on every open so rebinds show through immediately. The
// fixed rows mirror the keymap commands registered in main.ts (undo/redo,
// delete, save, door flip) and the mouse bindings of Orbit3D / the 2D plan
// controls / axis-snap orbit.
function buildSections(): { left: Section[]; right: Section[] } {
  const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
  const left: Section[] = [
    {
      group: "Modes & views",
      rows: [
        { keys: "Layout / Model", label: "Mode toggle, top of the sidebar" },
        { keys: k("view-toggle"), label: "Toggle 2D / 3D view" },
      ],
    },
    {
      group: "Camera — 3D views",
      rows: [
        { keys: "Right-drag", label: "Orbit (free tumble)" },
        { keys: "Middle-drag", label: "Pan" },
        { keys: "Scroll", label: "Zoom" },
        {
          keys: `${k("move-forward")} ${k("move-left")} ${k("move-back")} ${k("move-right")}`,
          label: "Fly (hold)",
        },
        { keys: `${k("move-up")} / ${k("move-down")}`, label: "Up / down (hold)" },
      ],
    },
    {
      group: "Camera — 2D views",
      rows: [
        { keys: "Middle-drag", label: "Pan" },
        { keys: "Scroll", label: "Zoom" },
        { keys: "Right-drag", label: "Snap-orbit between axis views" },
      ],
    },
    {
      group: "Layout mode",
      rows: [
        { keys: k("tool-select"), label: "Select tool" },
        { keys: k("layout-wall"), label: "Wall tool (click corners; Esc finishes)" },
        { keys: k("layout-measure"), label: "Measure tool" },
        { keys: "F / ⇧ F", label: "Flip selected door hinge / swing" },
        { keys: `${mod} D`, label: "Duplicate selection" },
        { keys: "Delete", label: "Delete selection" },
        { keys: "Esc", label: "Finish drawing · deselect" },
      ],
    },
  ];
  const right: Section[] = [
    {
      group: "Model mode — tools",
      rows: [
        { keys: k("tool-create"), label: "Create (draw voxels)" },
        { keys: k("tool-erase"), label: "Erase" },
        { keys: k("tool-select"), label: "Select (drag a rectangle / click an object)" },
        { keys: k("tool-move"), label: "Move" },
        { keys: k("tool-paint"), label: "Paint" },
        { keys: k("tool-bucket"), label: "Paint bucket" },
      ],
    },
    {
      group: "Model mode — shapes",
      rows: [
        { keys: k("shape-box"), label: "Box" },
        { keys: k("shape-extrude"), label: "Extrude" },
        { keys: k("shape-voxel"), label: "Voxel" },
        { keys: k("shape-circle"), label: "Circle" },
        { keys: k("shape-freeform"), label: "Freeform (Enter closes, Esc cancels)" },
      ],
    },
    {
      group: "Model mode — selection",
      rows: [
        { keys: k("select-invert"), label: "Invert selection" },
        { keys: `${mod} C / ${mod} V`, label: "Copy / paste" },
        { keys: `${mod} D`, label: "Duplicate" },
        { keys: "Delete", label: "Delete selected voxels" },
        { keys: "Esc", label: "Deselect" },
      ],
    },
    {
      group: "Everywhere",
      rows: [
        { keys: `${mod} Z`, label: "Undo" },
        { keys: `${mod} ⇧ Z / ${mod} Y`, label: "Redo" },
        { keys: `${mod} S`, label: "Save" },
        { keys: `${mod} ⇧ S`, label: "Save As" },
      ],
    },
  ];
  return { left, right };
}

export function installDesktopHelp(app: AppContext): { open(): void; toggle(): void } {
  injectStyles();

  let openRoot: HTMLElement | null = null;

  function close(): void {
    if (!openRoot) return;
    openRoot.remove();
    openRoot = null;
    document.removeEventListener("keydown", onKey, true);
  }

  // Capture-phase, like the Settings overlay: nothing behind the panel reacts
  // to keys while it's up. Esc or "?" closes; everything else is swallowed.
  const onKey = (e: KeyboardEvent): void => {
    e.stopPropagation();
    if (e.key === "Escape" || e.key === "?") {
      e.preventDefault();
      close();
    }
  };

  function open(): void {
    if (openRoot || app.isPresenting()) return;
    const root = document.createElement("div");
    root.className = "dt-help";
    openRoot = root;
    const card = document.createElement("div");
    card.className = "card";

    const head = document.createElement("div");
    head.className = "head";
    const h = document.createElement("h3");
    h.textContent = "Controls & shortcuts";
    const x = document.createElement("button");
    x.className = "x";
    x.textContent = "✕";
    x.title = "Close (Esc)";
    x.addEventListener("click", close);
    head.append(h, x);
    card.appendChild(head);

    const cols = document.createElement("div");
    cols.className = "cols";
    const { left, right } = buildSections();
    for (const sections of [left, right]) {
      const col = document.createElement("div");
      col.className = "col";
      for (const sec of sections) {
        const sub = document.createElement("div");
        sub.className = "sub";
        sub.textContent = sec.group;
        col.appendChild(sub);
        for (const row of sec.rows) {
          const r = document.createElement("div");
          r.className = "r";
          const lab = document.createElement("span");
          lab.className = "lab";
          lab.textContent = row.label;
          const kb = document.createElement("kbd");
          kb.textContent = row.keys;
          r.append(lab, kb);
          col.appendChild(r);
        }
      }
      cols.appendChild(col);
    }
    card.appendChild(cols);

    const foot = document.createElement("div");
    foot.className = "foot";
    foot.textContent =
      "? toggles this panel · Rebind keys in Settings (the account menu)";
    card.appendChild(foot);

    root.appendChild(card);
    root.addEventListener("mousedown", (e) => {
      if (e.target === root) close(); // backdrop dismiss
    });
    document.body.appendChild(root);
    document.addEventListener("keydown", onKey, true);
  }

  function toggle(): void {
    if (openRoot) close();
    else open();
  }

  // "?" toggles the panel. Registered like any other slice shortcut; the hub's
  // dispatcher already skips VR sessions and text inputs. (While the panel is
  // open its capture listener consumes the key, so this path opens it only.)
  // The clickable entry point lives in the account menu ("Controls & shortcuts").
  app.keymap.register({
    id: "help-toggle",
    when: (e) => e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey,
    run: (e) => {
      e.preventDefault();
      toggle();
    },
  });

  return { open, toggle };
}
